import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import {
  OpenerGroup,
  OpenerGroupCategoryRule,
  useCreateGroup,
  usePairCategories,
  useUpdateGroup
} from '../../api/useGroups';
import { categoryKey, conflict409Field } from './groupDialogHelpers';
import { FeatureFlagsSection } from './FeatureFlagsSection';
import { featureFlagsDiffer } from '../../api/useFeatureFlags';
import {
  COUNTER_STUCK_SECONDS_LABEL,
  counterStuckSecondsToInput,
  parseCounterStuckSecondsInput
} from '../shared/counterStuckSeconds';

const PRESET_COLORS = [
  '#e57373',
  '#ffb74d',
  '#fff176',
  '#81c784',
  '#4fc3f7',
  '#7986cb',
  '#ba68c8',
  '#f06292'
];

type Mode = 'blank' | 'category';

/** Tri-state boolean ↔ select-string. '' = inherit (null), 'on'/'off'. */
const gateToInput = (v: boolean | null | undefined): string =>
  v == null ? '' : v ? 'on' : 'off';
const gateToValue = (s: string): boolean | null =>
  s === 'on' ? true : s === 'off' ? false : null;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Present = edit (rename / color / sortOrder / dynamic); absent = create. */
  group?: OpenerGroup | null;
  /**
   * The group's desired pod state. Feature flags are delivered at pod BOOT, so
   * the backend refuses an effective change with a 409 while this is
   * on/panicked — the switches disable to match rather than letting the edit
   * bounce.
   */
  podsDesired?: 'on' | 'off' | 'panicked';
}

/**
 * Pull status + message out of an axios rejection so a 409 (duplicate name /
 * duplicate categoryRule) can be shown inline on the right field instead of
 * relying solely on the global mutation-error toast.
 */
function extractApiError(err: unknown): { status?: number; message: string } {
  const e = err as {
    response?: { status?: number; data?: { message?: string | string[] } };
    message?: string;
  };
  const raw = e?.response?.data?.message;
  const message =
    (Array.isArray(raw) ? raw.join('; ') : raw) || e?.message || 'Save failed';
  return { status: e?.response?.status, message };
}

/**
 * Create/edit dialog for opener pair groups. Create offers two modes —
 * Blank (name + optional color) and From category (seeds the group from a
 * category/subcategory bucket, optionally dynamic). Edit keeps the
 * categoryRule read-only and exposes name / color / sortOrder / dynamic /
 * quiet re-eval seconds / counter → market seconds. Both numeric fields
 * share the blank semantics: blank on EDIT sends explicit null (revert to
 * inherit / off); blank on CREATE omits the key.
 */
export const CreateGroupDialog = ({
  open,
  onClose,
  group,
  podsDesired
}: Props) => {
  const isEdit = !!group;
  const createMutation = useCreateGroup();
  const updateMutation = useUpdateGroup();

  const [mode, setMode] = useState<Mode>('blank');
  const [name, setName] = useState('');
  const [color, setColor] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [quietReevalSeconds, setQuietReevalSeconds] = useState('');
  const [stuckHedgeSeconds, setStuckHedgeSeconds] = useState('');
  const [walkStepSeconds, setWalkStepSeconds] = useState('');
  const [walkStepCents, setWalkStepCents] = useState('');
  const [lockstepAhead, setLockstepAhead] = useState('');
  // Per-platform placement-gate-disable defaults — tri-state encoded as a
  // string select: '' = inherit global (null), 'on' = true, 'off' = false.
  const [polyGate, setPolyGate] = useState('');
  const [kalshiGate, setKalshiGate] = useState('');
  const [dynamic, setDynamic] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [genericError, setGenericError] = useState<string | null>(null);

  const { data: categories, isLoading: categoriesLoading } = usePairCategories(
    open && !isEdit && mode === 'category'
  );

  useEffect(() => {
    if (!open) return;
    setMode('blank');
    setName(group?.name ?? '');
    setColor(group?.color ?? '');
    setSortOrder(group?.sortOrder != null ? String(group.sortOrder) : '');
    setQuietReevalSeconds(
      group?.makerQuietReevalSeconds != null
        ? String(group.makerQuietReevalSeconds)
        : ''
    );
    setStuckHedgeSeconds(
      counterStuckSecondsToInput(group?.counterStuckMarketHedgeSeconds)
    );
    setWalkStepSeconds(
      group?.counterMakerWalkStepSeconds != null
        ? String(group.counterMakerWalkStepSeconds)
        : ''
    );
    setWalkStepCents(
      group?.counterMakerWalkStepCents != null
        ? String(group.counterMakerWalkStepCents)
        : ''
    );
    setLockstepAhead(
      group?.lockstepMaxAheadBatches != null
        ? String(group.lockstepMaxAheadBatches)
        : ''
    );
    setPolyGate(gateToInput(group?.disablePlacementGatePolymarket));
    setKalshiGate(gateToInput(group?.disablePlacementGateKalshi));
    setDynamic(group?.dynamic ?? false);
    setFeatureFlags({ ...(group?.featureFlags ?? {}) });
    setSelectedCategory('');
    setNameError(null);
    setCategoryError(null);
    setGenericError(null);
  }, [open, group]);

  const selectedRule = useMemo<OpenerGroupCategoryRule | undefined>(() => {
    if (!selectedCategory) return undefined;
    const match = (categories ?? []).find(
      c => categoryKey(c.category, c.subcategory) === selectedCategory
    );
    if (!match) return undefined;
    return {
      category: match.category,
      ...(match.subcategory ? { subcategory: match.subcategory } : {})
    };
  }, [selectedCategory, categories]);

  const saving = createMutation.isLoading || updateMutation.isLoading;
  const canSave =
    !!name.trim() && (isEdit || mode === 'blank' || !!selectedRule) && !saving;

  const handleSave = async () => {
    setNameError(null);
    setCategoryError(null);
    setGenericError(null);
    const parsedStuck = parseCounterStuckSecondsInput(stuckHedgeSeconds);
    try {
      if (isEdit && group) {
        await updateMutation.mutateAsync({
          id: group._id,
          dto: {
            name: name.trim(),
            color: color || undefined,
            dynamic,
            ...(sortOrder.trim() !== '' && Number.isFinite(Number(sortOrder))
              ? { sortOrder: Number(sortOrder) }
              : {}),
            // Blank on edit reverts to inherit — send explicit null.
            ...(quietReevalSeconds.trim() === ''
              ? { makerQuietReevalSeconds: null }
              : Number.isFinite(Number(quietReevalSeconds))
                ? { makerQuietReevalSeconds: Number(quietReevalSeconds) }
                : {}),
            // Blank on edit clears the group fallback — explicit null.
            ...(parsedStuck.ok
              ? { counterStuckMarketHedgeSeconds: parsedStuck.value }
              : {}),
            ...(walkStepSeconds.trim() === ''
              ? { counterMakerWalkStepSeconds: null }
              : Number.isFinite(Number(walkStepSeconds))
                ? { counterMakerWalkStepSeconds: Number(walkStepSeconds) }
                : {}),
            ...(walkStepCents.trim() === ''
              ? { counterMakerWalkStepCents: null }
              : Number.isFinite(Number(walkStepCents))
                ? { counterMakerWalkStepCents: Number(walkStepCents) }
                : {}),
            ...(lockstepAhead.trim() === ''
              ? { lockstepMaxAheadBatches: null }
              : Number.isInteger(Number(lockstepAhead)) && Number(lockstepAhead) >= 0
                ? { lockstepMaxAheadBatches: Number(lockstepAhead) }
                : {}),
            // Tri-state: inherit ('') sends explicit null on edit so the group
            // reverts to the global default.
            disablePlacementGatePolymarket: gateToValue(polyGate),
            disablePlacementGateKalshi: gateToValue(kalshiGate),
            // ONLY when it changed. Sending it on every save would 400 the
            // whole edit whenever the map holds a key the registry no longer
            // declares — which is exactly the state a flag's retirement window
            // creates, and no control renders for a retired key, so the group
            // would be un-editable until someone ran the migration. It also
            // means this can ship before the backend without breaking edits.
            ...(featureFlagsDiffer(featureFlags, group.featureFlags)
              ? { featureFlags }
              : {})
          }
        });
      } else {
        await createMutation.mutateAsync({
          name: name.trim(),
          color: color || undefined,
          ...(quietReevalSeconds.trim() !== '' &&
          Number.isFinite(Number(quietReevalSeconds))
            ? { makerQuietReevalSeconds: Number(quietReevalSeconds) }
            : {}),
          ...(parsedStuck.ok && parsedStuck.value != null
            ? { counterStuckMarketHedgeSeconds: parsedStuck.value }
            : {}),
          ...(walkStepSeconds.trim() !== '' &&
          Number.isFinite(Number(walkStepSeconds))
            ? { counterMakerWalkStepSeconds: Number(walkStepSeconds) }
            : {}),
          ...(walkStepCents.trim() !== '' &&
          Number.isFinite(Number(walkStepCents))
            ? { counterMakerWalkStepCents: Number(walkStepCents) }
            : {}),
          ...(lockstepAhead.trim() !== '' &&
          Number.isInteger(Number(lockstepAhead)) && Number(lockstepAhead) >= 0
            ? { lockstepMaxAheadBatches: Number(lockstepAhead) }
            : {}),
          // Blank on create omits the key (inherit global); on/off send concrete.
          ...(polyGate !== ''
            ? { disablePlacementGatePolymarket: gateToValue(polyGate) }
            : {}),
          ...(kalshiGate !== ''
            ? { disablePlacementGateKalshi: gateToValue(kalshiGate) }
            : {}),
          ...(mode === 'category'
            ? { categoryRule: selectedRule, dynamic }
            : {})
        });
      }
      onClose();
    } catch (err) {
      const { status, message } = extractApiError(err);
      if (status === 409) {
        const field = conflict409Field(message);
        if (field === 'category') setCategoryError(message);
        else if (field === 'general') setGenericError(message);
        else setNameError(message);
      } else {
        setGenericError(message);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem' }}>
        {isEdit ? `Edit group — ${group?.name}` : 'New group'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {!isEdit && (
            <RadioGroup
              row
              value={mode}
              onChange={e => setMode(e.target.value as Mode)}
            >
              <FormControlLabel
                value="blank"
                control={<Radio size="small" />}
                label={
                  <Typography sx={{ fontSize: '0.8rem' }}>Blank</Typography>
                }
              />
              <FormControlLabel
                value="category"
                control={<Radio size="small" />}
                label={
                  <Typography sx={{ fontSize: '0.8rem' }}>
                    From category
                  </Typography>
                }
              />
            </RadioGroup>
          )}

          <TextField
            label="Name"
            size="small"
            value={name}
            onChange={e => {
              setName(e.target.value);
              setNameError(null);
            }}
            error={!!nameError}
            helperText={nameError ?? undefined}
            fullWidth
            autoFocus
          />

          <Box>
            <Typography sx={{ fontSize: '0.7rem', color: 'grey.500', mb: 0.5 }}>
              Color (optional — click again to clear)
            </Typography>
            <Stack direction="row" spacing={1}>
              {PRESET_COLORS.map(preset => (
                <Box
                  key={preset}
                  onClick={() => setColor(c => (c === preset ? '' : preset))}
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    backgroundColor: preset,
                    cursor: 'pointer',
                    border: theme =>
                      color === preset
                        ? `2px solid ${theme.palette.common.white}`
                        : '2px solid transparent'
                  }}
                />
              ))}
            </Stack>
          </Box>

          {isEdit && (
            <TextField
              label="Sort order"
              size="small"
              type="number"
              value={sortOrder}
              onChange={e => setSortOrder(e.target.value)}
              fullWidth
            />
          )}

          <TextField
            label="Quiet re-eval seconds"
            size="small"
            type="number"
            value={quietReevalSeconds}
            onChange={e => setQuietReevalSeconds(e.target.value)}
            helperText="Blank = default 30s · 10–20s live sports · up to 300s long-dated"
            fullWidth
          />

          <TextField
            label={COUNTER_STUCK_SECONDS_LABEL}
            size="small"
            type="number"
            value={stuckHedgeSeconds}
            onChange={e => setStuckHedgeSeconds(e.target.value)}
            helperText="Group fallback: counter maker unfilled after N s → cancel + hedge at market. Rule value wins · blank = off"
            fullWidth
          />

          <TextField
            label="Counter walk step (seconds)"
            size="small"
            type="number"
            value={walkStepSeconds}
            onChange={e => setWalkStepSeconds(e.target.value)}
            helperText="Blank = walk off. Both step-seconds and step-cents must be set."
            fullWidth
          />

          <TextField
            label="Counter walk step (cents)"
            size="small"
            type="number"
            value={walkStepCents}
            onChange={e => setWalkStepCents(e.target.value)}
            helperText="Cents to move toward market each step (e.g. 3 = $0.03)."
            fullWidth
          />

          <TextField
            label="Lockstep max ahead batches"
            size="small"
            type="number"
            value={lockstepAhead}
            onChange={e => setLockstepAhead(e.target.value)}
            helperText="0 = strict (wait for counter fill). 1 = one batch ahead. Blank = off / inherit. Rule override wins."
            fullWidth
          />

          <TextField
            select
            label="No gate — Polymarket"
            size="small"
            value={polyGate}
            onChange={e => setPolyGate(e.target.value)}
            helperText="Group default for the Polymarket placement gate. Inherit = use global. Rule override wins."
            fullWidth
          >
            <MenuItem value="">— inherit (global) —</MenuItem>
            <MenuItem value="on">On (disable gate)</MenuItem>
            <MenuItem value="off">Off (gate on)</MenuItem>
          </TextField>

          <TextField
            select
            label="No gate — Kalshi"
            size="small"
            value={kalshiGate}
            onChange={e => setKalshiGate(e.target.value)}
            helperText="Group default for the Kalshi placement gate. Inherit = use global. Rule override wins."
            fullWidth
          >
            <MenuItem value="">— inherit (global) —</MenuItem>
            <MenuItem value="on">On (disable gate)</MenuItem>
            <MenuItem value="off">Off (gate on)</MenuItem>
          </TextField>

          {isEdit && (
            <FeatureFlagsSection
              value={featureFlags}
              onChange={setFeatureFlags}
              resolved={group?.resolvedFeatureFlags}
              savedValue={group?.featureFlags}
              // Fail CLOSED: `undefined` means the pod-state query has not
              // resolved, errored, or omitted this group (the payload is
              // fail-soft). Enabling the controls on unknown state invites an
              // edit the backend will 409.
              locked={podsDesired !== 'off'}
              lockReason={
                podsDesired === undefined
                  ? 'Pod state unknown — flags are locked until it is known the group is stopped.'
                  : 'Stop the group to change flags — the set is delivered to the engine pod at boot.'
              }
            />
          )}

          {isEdit && group?.categoryRule && (
            <Typography sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
              Category rule: {group.categoryRule.category}
              {group.categoryRule.subcategory
                ? ` / ${group.categoryRule.subcategory}`
                : ''}{' '}
              (read-only)
            </Typography>
          )}

          {!isEdit && mode === 'category' && (
            <TextField
              select
              label="Category / subcategory"
              size="small"
              value={selectedCategory}
              onChange={e => {
                setSelectedCategory(e.target.value);
                setCategoryError(null);
              }}
              error={!!categoryError}
              helperText={categoryError ?? undefined}
              fullWidth
            >
              {categoriesLoading && (
                <MenuItem disabled value="">
                  Loading…
                </MenuItem>
              )}
              {(categories ?? []).map(c => (
                <MenuItem
                  key={categoryKey(c.category, c.subcategory)}
                  value={categoryKey(c.category, c.subcategory)}
                >
                  {c.category}
                  {c.subcategory ? ` / ${c.subcategory}` : ''} ({c.pairCount}{' '}
                  pairs)
                </MenuItem>
              ))}
            </TextField>
          )}

          {(isEdit ? !!group?.categoryRule : mode === 'category') && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={dynamic}
                  onChange={e => setDynamic(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: '0.8rem' }}>Dynamic</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'grey.500' }}>
                    Dynamic groups auto-assign future pairs matching the
                    category; fixed groups only assign current matches.
                  </Typography>
                </Box>
              }
            />
          )}

          {genericError && (
            <Typography sx={{ fontSize: '0.75rem', color: 'error.main' }}>
              {genericError}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          size="small"
        >
          {saving ? (
            <CircularProgress size={16} />
          ) : isEdit ? (
            'Save'
          ) : (
            'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
